import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ChangeEmailDialog } from '@/components/ChangeEmailDialog';
import { ContentMediaThumb } from '@/components/ContentMediaThumb';
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { MediaPreviewDialog } from '@/components/MediaPreviewDialog';
import { ResetPasswordDialog } from '@/components/ResetPasswordDialog';
import { StatusPill } from '@/components/StatusPill';
import { TransferAccountDialog } from '@/components/TransferAccountDialog';
import { UserChatsTab } from '@/components/UserChatsTab';
import { UserLedgerTab } from '@/components/UserLedgerTab';
import {
  signUserMedia,
  type AdminUserCard,
  type ChangeEmailResult,
  type TransferResult,
} from '@/lib/adminApi';
import {
  canChangeEmail,
  canDeleteAccount,
  canGrantCredits,
  canOpenUserMedia,
  canReadUserChats,
  canTransferAccount,
} from '@/lib/roles';
import { useUserCard, useUserContent } from '@/lib/useAdminUsers';
import { useMe } from '@/lib/useMe';

/**
 * `https://<product host>/workflows/<id>` — the "open in the product app"
 * link on each content row. `VITE_PRODUCT_ORIGIN` names the product host
 * explicitly (the admin SPA and the product app are DELIBERATELY different
 * origins, support-admin-proposal.md §3, so this cannot be derived from
 * `window.location` the way a same-origin link could); the same-host
 * fallback only fires in local dev, where no such env is set and the two
 * apps are commonly served side by side.
 */
const PRODUCT_ORIGIN =
  (import.meta.env.VITE_PRODUCT_ORIGIN as string | undefined) ?? window.location.origin;

function workflowUrl(pipelineOrWorkflowId: string): string {
  return `${PRODUCT_ORIGIN.replace(/\/+$/, '')}/workflows/${pipelineOrWorkflowId}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

type Tab = 'identity' | 'organization' | 'credits' | 'ledger' | 'flags' | 'content' | 'chats';

/**
 * The REAL `auth.identities.provider` value, given a readable face (ClickUp
 * 869f084w5 §1, which asked for "Google / Apple / email").
 *
 * Measured on dev 16.09.2026, the providers that actually occur are `email`,
 * `google` and `firebase` — **Apple does not appear at all**, because no
 * Apple sign-in is wired. So the raw value is shown alongside the label
 * rather than being mapped into a fixed three-way choice: a column that can
 * only ever print one of three names would have to invent one for
 * `firebase`, and would silently hide the day a fourth appears.
 */
function authMethodLabel(provider: string | null): string {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'email':
      return 'Email + password';
    case 'firebase':
      return 'Firebase';
    case null:
    case undefined:
      return 'Unknown';
    default:
      return provider;
  }
}

/**
 * The destructive-OUTLINE button, spelled once and shared by the two controls
 * on this card that carry it. Both open a wall; neither goes through one.
 *
 * MEASURED 22.09.2026 across every destructive button in apps/admin/src: this
 * app already spells two steps of the destructive role, and which step a
 * button gets is decided by what the CLICK does, with no counterexample —
 * OUTLINE opens the wall (this constant; UserPaymentsPage's "Preview
 * deletion"), SOLID `bg-destructive` goes through it (ReasonDialog's danger
 * confirm, TransferAccountDialog's "Transfer and erase this account",
 * DeleteAccountDialog's "Delete account permanently").
 *
 * So "Delete account" below is NOT promoted to the solid fill even though it
 * is the irreversible door: the button itself only opens a dialog, and
 * spending the loudest treatment on a safe click would leave the actual point
 * of no return looking no louder than the thing that opened it. What sets it
 * apart from the routine actions is SEPARATION, not a second red.
 *
 * A constant rather than two copies of the string: the row's whole point is
 * that these two look identical, and two literals drift.
 */
const DESTRUCTIVE_OUTLINE_BUTTON =
  'rounded-card border border-destructive-border px-4 py-2 text-body font-medium text-destructive-soft-foreground hover:bg-destructive-soft';

const TABS: { id: Tab; label: string }[] = [
  { id: 'identity', label: 'Identity' },
  { id: 'organization', label: 'Organization & plan' },
  { id: 'credits', label: 'Credits' },
  // Every role (support ticket 28.09.2026): the full coin ledger, because
  // Content lists runs and the bill is not only runs — see UserLedgerTab.
  { id: 'ledger', label: 'Coin ledger' },
  { id: 'flags', label: 'Flags' },
  { id: 'content', label: 'Content' },
];

/**
 * The owner's line (19.09.2026, ClickUp 869f4c8v4): a user's chats are the
 * superadmin's alone, so the tab is not rendered for anyone else — the API
 * would answer 403 anyway (`admin.user_chats.read`); this only keeps a door
 * that cannot open off the screen.
 */
const CHATS_TAB: { id: Tab; label: string } = { id: 'chats', label: 'Chats' };

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const [tab, setTab] = useState<Tab>('identity');
  const { data: card, isLoading, isError } = useUserCard(userId);
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [resetToast, setResetToast] = useState<string | null>(null);

  function showToast(message: string, ms = 5000) {
    setResetToast(message);
    window.setTimeout(() => setResetToast(null), ms);
  }

  function handleResetSuccess(deliveredTo: string) {
    showToast(`Temporary password sent to ${deliveredTo}.`);
  }

  /**
   * The card holds the OLD address until refetched, so the change is followed
   * by an invalidation rather than a local patch — what the server wrote is
   * the truth the page should show, including the auth method (a Google-only
   * account now signs in with a password).
   */
  function handleEmailChanged(result: ChangeEmailResult) {
    void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    const delivered = result.temp_password_delivered_to
      ? ` A temporary password was sent to ${result.temp_password_delivered_to}.`
      : '';
    showToast(
      result.warning
        ? `Login email changed to ${result.new_email}. ${result.warning}`
        : `Login email changed to ${result.new_email}. Both addresses were notified.${delivered}`,
      result.warning ? 12000 : 7000,
    );
  }

  /**
   * After a transfer this profile is gone (erased, or a tombstone) — the page
   * moves to the account that now holds everything, where the result reads
   * as the target's card rather than a "no such user" on the source's.
   */
  function handleTransferred(result: TransferResult) {
    void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    const erased =
      result.source_erasure.result === 'ok'
        ? `The source account was ${result.source_erasure.profile ?? 'erased'}.`
        : `The source login is closed but its erasure failed: ${result.source_erasure.error ?? 'unknown'}. Run Delete account on it.`;
    showToast(`Transferred to ${result.target_profile_id}. ${erased}`, 12000);
    navigate(`/users/${result.target_profile_id}`);
  }

  if (isLoading) {
    return <p className="text-body text-fg-hint">Loading…</p>;
  }
  if (isError) {
    return <p className="text-body text-destructive">Could not load this user. Try again.</p>;
  }
  if (card === null || card === undefined) {
    return <p className="text-body text-fg-hint">No such user.</p>;
  }

  return (
    <div className="min-w-0 [overflow-wrap:anywhere]">
      <h1 className="text-h2 font-semibold text-fg-primary">{card.name ?? card.email ?? card.id}</h1>
      <p className="mt-1 text-body text-fg-secondary">{card.email ?? '—'}</p>

      <div className="mt-4 flex flex-wrap gap-1 border-b border-border">
        {(canReadUserChats(me?.role) ? [...TABS, CHATS_TAB] : TABS).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-current={tab === t.id ? 'page' : undefined}
            className={`rounded-t-card px-3 py-2 text-body font-medium ${
              tab === t.id
                ? 'border-b-2 border-primary text-fg-primary'
                : 'text-fg-tertiary hover:text-fg-secondary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === 'identity' && (
          <dl className="grid max-w-lg grid-cols-1 gap-y-2 text-body sm:grid-cols-2 [&>dd]:min-w-0">
            <dt className="text-fg-secondary">Id</dt>
            <dd className="text-fg-primary">{card.id}</dd>
            <dt className="text-fg-secondary">Email</dt>
            <dd className="text-fg-primary">{card.email ?? '—'}</dd>
            <dt className="text-fg-secondary">Name</dt>
            <dd className="text-fg-primary">{card.name ?? '—'}</dd>
            <dt className="text-fg-secondary">Created</dt>
            <dd className="text-fg-primary">{formatDate(card.created_at)}</dd>
            <dt className="text-fg-secondary">Auth method</dt>
            <dd className="text-fg-primary">
              {authMethodLabel(card.auth_method)}
              {card.auth_method && (
                <span className="ml-2 text-micro text-fg-tertiary">({card.auth_method})</span>
              )}
            </dd>
            <dt className="text-fg-secondary">Platform</dt>
            <dd className="text-fg-primary">
              {card.platform === 'WEB' ? 'Web' : card.platform}
              <span className="ml-2 text-micro text-fg-tertiary">
                the only platform that exists — no App client, and every payment row is Web
              </span>
            </dd>
            <dt className="text-fg-secondary">Last activity</dt>
            <dd className="text-fg-primary">{formatDate(card.last_activity)}</dd>
          </dl>
        )}

        {tab === 'identity' && (
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setResetDialogOpen(true)}
              className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover"
            >
              Reset password
            </button>
            {canChangeEmail(me?.role) && !card.flags.deleted && (
              <button
                type="button"
                onClick={() => setChangeEmailOpen(true)}
                className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover"
              >
                Change email
              </button>
            )}
            {canTransferAccount(me?.role) && !card.flags.deleted && (
              <button
                type="button"
                onClick={() => setTransferOpen(true)}
                className={DESTRUCTIVE_OUTLINE_BUTTON}
              >
                Transfer to another account
              </button>
            )}
          </div>
        )}

        {/* THE DELETE DOOR, ON THE CARD (ClickUp 869f5q8kd, 22.09.2026).
            It always rendered for `support` — on `/users/:id/payments`, a
            route nothing here linked to, reachable only by pasting a raw
            profile id into the Payments lookup. "I can see Transfer, but cannot
            find Delete anywhere" was a navigation report, not a permission
            one, so the fix is a handle on the door, not a wider grant.

            Its own block after a rule, NOT a fourth pill in the row above:
            the three up there are recoverable and this one is not, and an
            irreversible control that queues up beside routine ones is read as
            routine. The button's own treatment is the row's existing
            destructive outline (DESTRUCTIVE_OUTLINE_BUTTON) — see that
            constant for why it is not promoted to the solid fill.

            Every guard behind it is unchanged and lives in the dialog: a
            mandatory reason, a preview of what will be erased as its own
            step, and the typed-email confirm wall. */}
        {tab === 'identity' && canDeleteAccount(me?.role) && !card.flags.deleted && (
          <div className="mt-6 border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className={DESTRUCTIVE_OUTLINE_BUTTON}
            >
              Delete account
            </button>
            <p className="mt-2 text-meta text-fg-tertiary">
              Irreversible. Opens a preview of everything that will be erased; the deletion itself
              needs a reason and the account&apos;s email typed back.
            </p>
          </div>
        )}

        {tab === 'organization' && (
          <div>
            <dl className="grid max-w-lg grid-cols-1 gap-y-2 text-body sm:grid-cols-2 [&>dd]:min-w-0">
              <dt className="text-fg-secondary">Organization</dt>
              <dd className="text-fg-primary">{card.organization?.name ?? '—'}</dd>
              <dt className="text-fg-secondary">Role</dt>
              <dd className="text-fg-primary">{card.role ?? '—'}</dd>
              <dt className="text-fg-secondary">Plan</dt>
              <dd className="text-fg-primary">{card.plan?.name ?? '—'}</dd>
              <dt className="text-fg-secondary">Workspaces</dt>
              <dd className="text-fg-primary">
                {card.workspaces.length > 0
                  ? card.workspaces.map((w) => w.name ?? w.id).join(', ')
                  : '—'}
              </dd>
            </dl>
            {card.organization_history.length > 0 && (
              <div className="mt-6">
                <h2 className="text-h3 font-semibold text-fg-primary">Organization history</h2>
                <div className="mt-2 overflow-x-auto" role="region" aria-label="Organization history" tabIndex={0}>
                  <table className="min-w-full whitespace-nowrap text-left text-body">
                    <thead>
                      <tr className="border-b border-border text-meta text-fg-secondary">
                        <th className="py-1 pr-4">Organization</th>
                        <th className="py-1 pr-4">Role</th>
                        <th className="py-1 pr-4">Plan</th>
                        <th className="py-1 pr-4">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.organization_history.map((h) => (
                        <tr key={`${h.organization_id}-${h.joined_at}`} className="border-b border-border last:border-0">
                          <td className="py-1 pr-4 text-fg-primary">{h.name ?? '—'}</td>
                          <td className="py-1 pr-4 text-fg-secondary">{h.role ?? '—'}</td>
                          <td className="py-1 pr-4 text-fg-secondary">{h.plan_name ?? '—'}</td>
                          <td className="py-1 pr-4 text-fg-secondary">{formatDate(h.joined_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'credits' && <CreditsTab card={card} canGrant={canGrantCredits(me?.role)} />}

        {tab === 'ledger' && userId && <UserLedgerTab userId={userId} />}

        {tab === 'flags' && (
          <dl className="grid max-w-lg grid-cols-1 gap-y-2 text-body sm:grid-cols-2 [&>dd]:min-w-0">
            <dt className="text-fg-secondary">Deleted / tombstoned</dt>
            <dd className="text-fg-primary">{card.flags.deleted ? 'Yes' : 'No'}</dd>
            <dt className="text-fg-secondary">Impersonation allowed</dt>
            <dd className="text-fg-primary">{card.flags.impersonation_allowed ? 'Yes' : 'No'}</dd>
          </dl>
        )}

        {tab === 'content' && userId && (
          <UserContentTab userId={userId} canOpenMedia={canOpenUserMedia(me?.role)} />
        )}

        {tab === 'chats' && userId && canReadUserChats(me?.role) && (
          <UserChatsTab userId={userId} />
        )}
      </div>

      {resetDialogOpen && userId && (
        <ResetPasswordDialog
          userId={userId}
          knownEmail={card.email ?? undefined}
          onClose={() => setResetDialogOpen(false)}
          onSuccess={handleResetSuccess}
        />
      )}

      {changeEmailOpen && userId && (
        <ChangeEmailDialog
          userId={userId}
          currentEmail={card.email ?? undefined}
          onClose={() => setChangeEmailOpen(false)}
          onSuccess={handleEmailChanged}
        />
      )}

      {transferOpen && userId && (
        <TransferAccountDialog
          userId={userId}
          sourceEmail={card.email ?? undefined}
          onClose={() => setTransferOpen(false)}
          onSuccess={handleTransferred}
        />
      )}

      {deleteOpen && userId && (
        <DeleteAccountDialog
          userId={userId}
          email={card.email}
          onClose={() => setDeleteOpen(false)}
          onSuccess={(outcome) => {
            // The card is now a tombstone (or gone): re-read rather than
            // patching a local flag, so the buttons that must disappear —
            // this one included — disappear because the SERVER says so.
            showToast(`Account ${outcome}.`);
            void queryClient.invalidateQueries({ queryKey: ['admin-users', 'card', userId] });
          }}
        />
      )}

      {resetToast && (
        <div
          role="status"
          className="fixed bottom-6 right-6 rounded-card border border-success-border bg-success-soft px-4 py-3 text-body text-success-soft-foreground shadow-elev-2"
        >
          {resetToast}
        </div>
      )}
    </div>
  );
}

/**
 * The Credits tab, and the one click from here to where credits are actually
 * granted (ClickUp 869f084w5, owner 17.09.2026).
 *
 * THE DEFECT THIS FIXES was navigation, not capability: the grant screen has
 * always existed at `/organizations/:orgId/credits` — amount, a reason with a
 * minimum length, platform, a confirm dialog, one audit row, one idempotency
 * key, a page above the notify threshold — but the card a support person
 * starts from (a USER, because a complaint arrives as an email address)
 * printed a balance and stopped. The organization id was not on the card
 * either, so the /organizations jump-page, which wants one pasted by hand,
 * could not be reached from here at all.
 *
 * It is a LINK, deliberately, not a second grant form: the rules that matter
 * here (reason floor, receipt, idempotency, notification) are worth exactly
 * one implementation, and a second form on the card would be a second place
 * for them to drift.
 *
 * The id is shown as well as linked because it is what every other surface —
 * the Organizations lookup, the grants log's Organization filter, a SQL
 * question — asks for, and a user's card is where a human first has it.
 */
function CreditsTab({ card, canGrant }: { card: AdminUserCard; canGrant: boolean }) {
  const organization = card.organization;
  return (
    <div className="max-w-lg">
      <p className="text-body text-fg-primary">
        {card.credits_remaining !== null
          ? `${card.credits_remaining} credits remaining`
          : 'Credits remaining not available for this workspace.'}
      </p>

      {organization ? (
        <div className="mt-4 rounded-card border border-border bg-surface-field p-4">
          <div className="text-meta text-fg-secondary">Organization (credits are held here, not by the person)</div>
          <div className="mt-1 text-body text-fg-primary">{organization.name ?? '—'}</div>
          <div className="mt-1 break-all font-mono text-micro text-fg-tertiary">{organization.id}</div>
          {canGrant ? (
            <Link
              to={`/organizations/${organization.id}/credits`}
              className="mt-3 inline-flex rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover"
            >
              Grant credits
            </Link>
          ) : null}
          {canGrant ? (
            // The coin-refund calculator (869f7jkj5) — where L1 lands from a
            // refund ticket, which starts at the person, not the org.
            <Link
              to={`/organizations/${organization.id}/refund-calculator`}
              className="ml-2 mt-3 inline-flex rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover"
            >
              Refund calculator
            </Link>
          ) : (
            <p className="mt-3 text-micro text-fg-tertiary">
              Your console account is read-only. Granting credits needs the support, finance or
              superadmin role.
            </p>
          )}
        </div>
      ) : (
        <p className="mt-4 text-body text-fg-secondary">
          This user belongs to no organization — and credits are held by the organization, so there
          is nothing to grant against yet.
        </p>
      )}
    </div>
  );
}

/**
 * Why the failure reason is its OWN column rather than a line under the status
 * pill (ClickUp 869f084w5 §4: "for failed generations, the reason for the
 * error, where available"): support scans this table for the ONE row a
 * complaint is about,
 * and a reason tucked under a pill in a status cell is not scannable. The
 * TYPED class comes first because it is the part that answers the actual
 * question — was this the user's input (`bad-input`, `content-refused`) or
 * ours (`provider-timeout`, `auth`, `render-engine-crashed`) — with the raw
 * message underneath for the detail.
 *
 * `error_class` is null on many real failures (measured on dev 16.09.2026:
 * 55 of 155 failed node runs carry one), so the message alone is a legitimate
 * answer; "—" appears only when the run carries neither, which is what the
 * request's own "where available" allows for.
 */
function FailureReason({
  errorClass,
  errorMessage,
  status,
}: {
  errorClass: string | null;
  errorMessage: string | null;
  status: string | null;
}) {
  const failed = status !== 'succeed' && status !== 'processing';
  if (!errorClass && !errorMessage) {
    return <span className="text-micro text-fg-tertiary">{failed ? 'Not recorded' : '—'}</span>;
  }
  return (
    <div className="max-w-xs">
      {errorClass && (
        <span className="inline-block rounded-full bg-destructive-soft px-2 py-0.5 text-micro font-medium text-destructive-soft-foreground">
          {errorClass}
        </span>
      )}
      {errorMessage && (
        <div className="mt-1 text-micro text-fg-secondary">{errorMessage}</div>
      )}
    </div>
  );
}

/**
 * The content type of a row, from the extension of the storage key its run
 * produced (ClickUp 869f084w5 §4 asks for "date, content type, generation
 * status and credits spent" — the table already had three of the four, with
 * the run KIND standing in for the type).
 *
 * Derived here rather than added to the API because the key is already on the
 * row and the extension is the only signal either side has: `tala_nodes_runs`
 * carries no media-type column, and a pipeline-run row has no output of its
 * own at all — which is why an unknown type reads as "—" instead of guessing
 * from the workflow's name.
 */
function contentType(mediaKey: string | null): string {
  if (!mediaKey) return '—';
  const ext = mediaKey.split('.').pop()?.toLowerCase() ?? '';
  if (['mp4', 'mov', 'webm', 'm4v', 'mkv'].includes(ext)) return 'Video';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'].includes(ext)) return 'Image';
  if (['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg'].includes(ext)) return 'Audio';
  if (['srt', 'vtt', 'json', 'txt'].includes(ext)) return 'Text';
  return ext ? ext.toUpperCase() : '—';
}

/**
 * The content history as ROWS is every role's (date, workflow, type, status,
 * failure reason, credits); the media ITSELF — the thumbnail and the
 * full-size preview, both served by POST /api/admin/v2/media/sign — is the
 * superadmin's alone (`canOpenMedia`). Below that role the Preview column is
 * not rendered and the sign call is never made: a column of "n/a" would read
 * as a broken page, and a 403 per page load as an error.
 */
function UserContentTab({ userId, canOpenMedia }: { userId: string; canOpenMedia: boolean }) {
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  const { data, isLoading, isError } = useUserContent(userId, cursor);
  const [signedUrls, setSignedUrls] = useState<Record<string, string | null>>({});
  const [preview, setPreview] = useState<string | null>(null);

  const rows = data?.rows ?? [];

  useEffect(() => {
    if (!canOpenMedia) return;
    const keys = rows.map((r) => r.media_key).filter((k): k is string => Boolean(k));
    const unresolved = keys.filter((k) => !(k in signedUrls));
    if (unresolved.length === 0) return;
    let cancelled = false;
    void signUserMedia(userId, unresolved)
      .then((urls) => {
        if (cancelled) return;
        setSignedUrls((prev) => ({ ...prev, ...urls }));
      })
      .catch(() => {
        if (cancelled) return;
        setSignedUrls((prev) => ({
          ...prev,
          ...Object.fromEntries(unresolved.map((k) => [k, null])),
        }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the row set, not signedUrls itself
  }, [userId, rows, canOpenMedia]);

  // Runs are not the whole bill: agent turns have no run, and a deleted
  // workflow takes its runs with it while the ledger keeps the charge
  // (support ticket 28.09.2026). Said on the tab itself so "no content" is
  // never read as "nothing was spent".
  const ledgerNote = (
    <p className="mb-3 text-micro text-fg-tertiary">
      Runs this user still owns. Agent turns and charges of deleted workflows are not runs — see
      the Coin ledger tab for every debit.
    </p>
  );

  if (isLoading) return <p className="text-body text-fg-hint">Loading…</p>;
  if (isError) return <p className="text-body text-destructive">Could not load content. Try again.</p>;
  if (rows.length === 0) {
    return (
      <div>
        {ledgerNote}
        <p className="text-body text-fg-hint">No content yet.</p>
      </div>
    );
  }

  return (
    <div>
      {ledgerNote}
      <div className="overflow-x-auto rounded-card border border-border">
        <table className="w-full text-left text-body">
          <thead>
            <tr className="border-b border-border bg-surface-muted text-meta text-fg-secondary">
              {canOpenMedia && <th className="px-3 py-2">Preview</th>}
              <th className="px-3 py-2">Workflow</th>
              <th className="px-3 py-2">Content type</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Failure reason</th>
              <th className="px-3 py-2">Credits</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Open</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.kind}-${row.id}`} className="border-b border-border last:border-0">
                {canOpenMedia && (
                  <td className="px-3 py-2">
                    {row.media_key && signedUrls[row.media_key] ? (
                      <button
                        type="button"
                        onClick={() => setPreview(row.media_key)}
                        aria-label="Open full-size preview"
                        className="block rounded-card focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid="content-preview-open"
                      >
                        <ContentMediaThumb
                          mediaKey={row.media_key}
                          url={signedUrls[row.media_key]}
                        />
                      </button>
                    ) : (
                      <ContentMediaThumb
                        mediaKey={row.media_key}
                        url={row.media_key ? signedUrls[row.media_key] : null}
                      />
                    )}
                  </td>
                )}
                <td className="px-3 py-2 text-fg-primary">{row.workflow_name ?? '—'}</td>
                <td className="px-3 py-2 text-fg-secondary">{contentType(row.media_key)}</td>
                <td className="px-3 py-2 text-fg-secondary capitalize">{row.kind.replace('_', ' ')}</td>
                <td className="px-3 py-2">
                  <StatusPill status={row.status} />
                </td>
                <td className="px-3 py-2 align-top">
                  <FailureReason
                    errorClass={row.error_class}
                    errorMessage={row.error_message}
                    status={row.status}
                  />
                </td>
                <td className="px-3 py-2 text-fg-secondary">{row.credits_charged}</td>
                <td className="px-3 py-2 text-fg-secondary">{formatDate(row.date_created)}</td>
                <td className="px-3 py-2">
                  <a
                    href={workflowUrl(row.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-meta font-medium text-primary hover:underline"
                  >
                    Open
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={cursorStack.length <= 1}
          onClick={() => setCursorStack((s) => s.slice(0, -1))}
          className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={!data?.next}
          onClick={() => setCursorStack((s) => [...s, data?.next ?? null])}
          className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
      {preview && (
        <MediaPreviewDialog
          mediaKey={preview}
          url={signedUrls[preview] ?? null}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
